import {
  numberRegex,
  symbolRegex,
  emailRegex,
  lowerCaseIncludes,
  containsUpperAndLowerCase,
  policySymbolRegex,
  policyAllowedCharactersRegex,
  tripleRepeatRegex,
  ascendingLettersRegex,
  ascendingNumbersRegex,
  descendingNumbersRegex,
  repeatedNumberTripletsRegex,
} from './helpers';

/** @import { ValidationRule, ValidationRules } from '../components/Form/TextInput/ValidatedInputTypes' */

export const numberTest = () => (/** @type {string} */ value) => numberRegex.test(value);
export const symbolTest = () => (/** @type {string} */ value) => symbolRegex.test(value);
const casingTest = () => (/** @type {string} */ value) => containsUpperAndLowerCase(value);
const characterLengthTest = (maxLength = 10) => (/** @type {string} */ value) => value.length >= maxLength;
// const nameNotContainedTest = (/** @type {string} */ name, /** @type {string} */ email) => {
//   return (/** @type {string} */ value) => {
//     return !lowerCaseIncludes(value, name) && !lowerCaseIncludes(value, email);
//   };
// };

const valuesToTestNotContained = (/** @type {string[]} */ ...valuesToTest) => {
  return (/** @type {string} */ value) => {
    return valuesToTest
      .filter(valueToTest => valueToTest) // don't test empty string / null / undefined
      .every(valueToTest => !lowerCaseIncludes(value, valueToTest));
  };
};

export const postCodeRegex = new RegExp('([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))s?[0-9][A-Za-z]{2})');

export const postCodeValidation = (/** @type {string} */ value) => {
  const postcodeRegEx = /^[A-Z]{1,2}\d[A-Z\d]? ?[0-9][A-Z]{2}$/i;
  return postcodeRegEx.test(value);
};

export const postCodeTest = (/** @type {string} */ value) => postCodeRegex.test(value.replace(/\s/g, ''));

// National Insurance Number validation
// Format: 2 letters, 6 numbers, 1 letter (e.g., QQ 12 34 56 B)
export const nationalInsuranceRegex = /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{1}$/;

export const nationalInsuranceTest = (/** @type {string} */ value) => {
  // Remove spaces and convert to uppercase for validation
  const cleanValue = value.replace(/\s/g, '').toUpperCase();
  
  // If empty, consider it valid (no error)
  if (!cleanValue) return true;
  
  // Progressive validation based on length
  const len = cleanValue.length;
  
  // Check first character (must be valid letter)
  if (len >= 1) {
    const firstChar = cleanValue[0];
    if (!/[A-CEGHJ-PR-TW-Z]/.test(firstChar)) return false;
  }
  
  // Check second character (must be valid letter, not O)
  if (len >= 2) {
    const secondChar = cleanValue[1];
    if (!/[A-CEGHJ-NPR-TW-Z]/.test(secondChar)) return false;
    
    // Check for forbidden prefixes
    const prefix = cleanValue.substring(0, 2);
    const forbiddenPrefixes = [
      'BG', 'GB', 'KN', 'NK', 'NT', 'TN', 'ZZ',
      'OO', 'FY', 'NC', 'PP', 'PZ', 'PY'
    ];
    if (forbiddenPrefixes.includes(prefix)) return false;
  }
  
  // Check digits (positions 3-8)
  if (len >= 3) {
    for (let i = 2; i < Math.min(len, 8); i++) {
      if (!/[0-9]/.test(cleanValue[i])) return false;
    }
  }
  
  // Check final character (must be A, B, C, or D)
  if (len >= 9) {
    const lastChar = cleanValue[8];
    if (!/[A-D]/.test(lastChar)) return false;
  }
  
  // If more than 9 characters, it's invalid
  if (len > 9) return false;
  
  return true;
};

// Strict validation for form completion - requires complete 9-character format
export const nationalInsuranceCompleteTest = (/** @type {string} */ value) => {
  // Remove spaces and convert to uppercase for validation
  const cleanValue = value.replace(/\s/g, '').toUpperCase();
  
  // Must be exactly 9 characters
  if (cleanValue.length !== 9) return false;
  
  // Use the original regex for complete validation
  if (!nationalInsuranceRegex.test(cleanValue)) return false;
  
  // Check for forbidden prefixes
  const forbiddenPrefixes = [
    'BG', 'GB', 'KN', 'NK', 'NT', 'TN', 'ZZ',
    'OO', 'FY', 'NC', 'PP', 'PZ', 'PY'
  ];
  const prefix = cleanValue.substring(0, 2);
  if (forbiddenPrefixes.includes(prefix)) return false;
  
  return true;
};

export const getNationalInsuranceValidationRules = () => ({
  nationalInsuranceNumber: {
    label: "Please enter a valid National Insurance number",
    test: nationalInsuranceTest
  }
});

const emailTest = (/** @type {string} */ value) => emailRegex.test(value);
const investecEmail = new RegExp('investec')
const investecStaffEmailTest = (/** @type {string} */ value) => !investecEmail.test(value);

/** @type {() => ValidationRules} */
export const getEmailValidationRule = () => ({
  email: {
    label: 'Must be a valid email address',
    test: emailTest
  }
});

/** @type {(blockInvestecStaffEmail?: boolean) => ValidationRule[]} */
export const getEmailValidationRules = (blockInvestecStaffEmail = true) => {
  let rules = [
      {
        label: "That doesn't look right, please try again",
        test: emailTest
      }];
  if (blockInvestecStaffEmail) {
    rules = [
      ...rules,
      {
        label: "An Investec email address cannot be used when creating your online profile. This is to ensure compliance with Financial Services Compensation Scheme (FSCS) regulations, which stipulate that your registered email address should not be linked to the deposit taking institution.",
        test: investecStaffEmailTest
      }
    ];
  }
  return rules;
};

/** @type {(firstName: string, lastName: string, email: string) => ValidationRules} */
export const getPasswordValidationRules = (firstName, lastName, email) => ({
  length: {
    label: 'At least 10 characters',
    test: characterLengthTest(10)
  },
  upperAndLowercaseLetter: {
    label: 'At least one upper and lowercase letter',
    test: casingTest()
  },
  number: {
    label: 'Includes at least one number',
    test: numberTest()
  },
  symbol: {
    label: 'Includes at least one symbol',
    test: symbolTest()
  },
  containsNameEmail: {
    label: 'Isn\'t the same as your username (email address)',
    test: valuesToTestNotContained(firstName, lastName, email)
  },
});

/** @type {(password: string) => ValidationRules} */
export const getPasswordConfirmationValidationRules = (/** @type {string} */ password) => ({
  matchesPassword: {
    label: 'Password match',
    test: (/** @type {string} */ value) => value === password
  }
});

// ── Password Policy v2 ────────────────────────────────────────────────────────

export const characterRangeLengthTest = (/** @type {number} */ min, /** @type {number} */ max) => (/** @type {string} */ value) => value.length >= min && value.length <= max;

export const noWhitespaceTest = () => (/** @type {string} */ value) => /^\S+$/.test(value);

export const policySymbolTest = () => (/** @type {string} */ value) => policySymbolRegex.test(value);

export const allowedCharactersTest = () => (/** @type {string} */ value) => policyAllowedCharactersRegex.test(value);

export const hasSymbolTest = () => {
  const tests = [policySymbolTest(), allowedCharactersTest()];
  return (/** @type {string} */ value) => tests.every(test => test(value));
};

export const noTripleRepeatTest = () => (/** @type {string} */ value) => !tripleRepeatRegex.test(value);

export const noAscendingLettersTest = () => (/** @type {string} */ value) => !ascendingLettersRegex.test(value);

export const noAscendingNumbersTest = () => (/** @type {string} */ value) => !ascendingNumbersRegex.test(value);

export const noDescendingNumbersTest = () => (/** @type {string} */ value) => !descendingNumbersRegex.test(value);

export const noRepeatedNumberTripletsTest = () => (/** @type {string} */ value) => !repeatedNumberTripletsRegex.test(value);

export const noObviousPatternsTest = () => {
  const patternTests = [
    noAscendingLettersTest(),
    noAscendingNumbersTest(),
    noDescendingNumbersTest(),
  ];

  return (/** @type {string} */ value) => patternTests.every(test => test(value));
};
export const emailReferenceTest = (/** @type {string} */ email) => (/** @type {string} */ value) => {
  if (!email) return true;
  const passwordLower = value.toLowerCase();
  const emailLower = email.toLowerCase();
  const username = emailLower.split('@')[0];
  const normalizedUsername = username.replace(/[^a-z0-9]/g, '');
  const normalizedPassword = passwordLower.replace(/[^a-z0-9]/g, '');
  if (normalizedUsername.length < 3) return true;
  return !passwordLower.includes(emailLower) && !normalizedPassword.includes(normalizedUsername);
};

/** @type {(email: string) => ValidationRules} */
export const getPasswordValidationRulesUpdated = (/** @type {string} */ email) => ({
  length_10_40: {
    label: 'At least 10 characters',
    test: characterRangeLengthTest(10, 40)
  },
  
  has_upper_and_lower: {
    label: 'At least one upper and lowercase letter',
    test: casingTest()
  },
  has_number: {
    label: 'Includes at least one number',
    test: numberTest()
  },
  has_symbol: {
    label: 'Includes at least one symbol',
    test: hasSymbolTest()
  },
  no_email_address: {
    label: "Isn't the same as your username (email address) ",
    test: emailReferenceTest(email)
  },
  no_triple_repeats: {
    label: 'No repeated characters (aaa, 111)',
    test: noTripleRepeatTest()
  },
  no_obvious_patterns: {
    label: 'No sequential characters (abc, 123)',
    test: noObviousPatternsTest()
  },
  no_whitespace: {
    label:'Doesn’t include spaces',
    test: noWhitespaceTest()
  },
  no_ascending_letters: {
    label:'',// 'No sequential letters (e.g. abc, xyz)',
    test: noAscendingLettersTest()
  },
  no_ascending_numbers: {
    label:'',// 'No ascending number sequences (e.g. 123, 456)',
    test: noAscendingNumbersTest()
  },
  no_descending_numbers: {
    label:'',// 'No descending number sequences (e.g. 321, 654)',
    test: noDescendingNumbersTest()
  },
  no_repeated_number_triplets: {
    label:'',// 'No repeated number triplets (e.g. 111, 000)',
    test: noRepeatedNumberTripletsTest()
  },
});

export const checkValidation = (/** @type {Record<string, { label: string; test: (value: string) => boolean; }>} */ rules, /** @type {string} */ value) => Object.values(rules).every(({ test }) => test(value))
